import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  Handshake,
  Search,
  Send,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  Bell,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CityCollaborationService } from "@/services/APIs";
import type { CollaborationResponse } from "@/services/APIs/CityCollaborationService";

type CollaborationStatus = "pending" | "active" | "suspended" | "ended";

interface Collaboration {
  id: string;
  initiator_org_id: string;
  partner_org_id: string;
  collaboration_name: string;
  scope: string[];
  status: CollaborationStatus;
  invitation_sent_at: string | null;
  accepted_at: string | null;
  created_at: string;
  initiator_org?: { name: string };
  partner_org?: { name: string };
}

// Mock collaborations data
const initialCollaborations: Collaboration[] = [
  {
    id: "collab-1",
    initiator_org_id: "org-1",
    partner_org_id: "org-2",
    collaboration_name: "Collaboration with Metro County Government",
    scope: ["general", "infrastructure"],
    status: "active",
    invitation_sent_at: new Date(
      Date.now() - 7 * 24 * 60 * 60 * 1000
    ).toISOString(),
    accepted_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    initiator_org: { name: "City of Springfield" },
    partner_org: { name: "Metro County Government" },
  },
  {
    id: "collab-2",
    initiator_org_id: "org-3",
    partner_org_id: "org-1",
    collaboration_name: "Collaboration with City of Springfield",
    scope: ["general"],
    status: "pending",
    invitation_sent_at: new Date(
      Date.now() - 2 * 24 * 60 * 60 * 1000
    ).toISOString(),
    accepted_at: null,
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    initiator_org: { name: "Riverside City Council" },
    partner_org: { name: "City of Springfield" },
  },
  {
    id: "collab-3",
    initiator_org_id: "org-1",
    partner_org_id: "org-4",
    collaboration_name: "Collaboration with Greenfield Municipality",
    scope: ["general", "technology"],
    status: "pending",
    invitation_sent_at: new Date(
      Date.now() - 1 * 24 * 60 * 60 * 1000
    ).toISOString(),
    accepted_at: null,
    created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    initiator_org: { name: "City of Springfield" },
    partner_org: { name: "Greenfield Municipality" },
  },
];

const statusColors: Record<CollaborationStatus, string> = {
  pending: "bg-warning/10 text-warning border-warning/20",
  active: "bg-success/10 text-success border-success/20",
  suspended: "bg-destructive/10 text-destructive border-destructive/20",
  ended: "bg-muted text-muted-foreground border-muted",
};

export default function CityCollaborations() {
  const [activeTab, setActiveTab] = useState("organizations");
  const [searchQuery, setSearchQuery] = useState("");
  const [, setCollaborations] = useState<Collaboration[]>(
    initialCollaborations
  );
  const [processingOrgId, setProcessingOrgId] = useState<string | null>(null);
  const [cancelingCollaborationId, setCancelingCollaborationId] = useState<
    string | null
  >(null);
  const [acceptingCollaborationId, setAcceptingCollaborationId] = useState<
    string | null
  >(null);
  const [rejectingCollaborationId, setRejectingCollaborationId] = useState<
    string | null
  >(null);
  const [deactivatingCollaborationId, setDeactivatingCollaborationId] =
    useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch organizations from API
  const { data: organizations = [], isLoading: isLoadingOrgs } = useQuery({
    queryKey: ["city-collaborations-orgs", searchQuery],
    queryFn: async () => {
      return await CityCollaborationService.getOrganizations(searchQuery);
    },
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Fetch collaboration counts from API
  const {
    data: collaborationCounts,
    isLoading: isLoadingCounts,
    isError: isErrorCounts,
  } = useQuery({
    queryKey: ["city-collaborations-count"],
    queryFn: async () => {
      return await CityCollaborationService.getCollaborationCounts();
    },
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Fetch collaborations from API
  const { data: apiCollaborations = [], isLoading: isLoadingCollaborations } =
    useQuery({
      queryKey: ["city-collaborations"],
      queryFn: async () => {
        return await CityCollaborationService.getCollaborations();
      },
      retry: 1,
      refetchOnWindowFocus: false,
    });

  // Get login_org_id from collaboration counts API
  const loginOrgId = collaborationCounts?.login_org_id ?? null;

  // Console log to verify login_org_id extraction
  useEffect(() => {
    if (collaborationCounts) {
      console.log("Collaboration counts loaded:", collaborationCounts);
      console.log("login_org_id extracted from count API:", loginOrgId);
    }
  }, [collaborationCounts, loginOrgId]);

  // Mutation for creating collaboration
  const createCollaborationMutation = useMutation({
    mutationFn: async (payload: {
      sender_organization_id: string;
      receiver_organization_id: string;
    }) => {
      return await CityCollaborationService.createCollaboration(payload);
    },
    onSuccess: () => {
      toast.success("Collaboration request sent successfully!", {
        description: "The organization will be notified of your request.",
      });
      // Clear processing state
      setProcessingOrgId(null);
      // Refetch organizations to get updated status
      queryClient.invalidateQueries({ queryKey: ["city-collaborations-orgs"] });
      queryClient.invalidateQueries({
        queryKey: ["city-collaborations-count"],
      });
      queryClient.invalidateQueries({ queryKey: ["city-collaborations"] });
    },
    onError: (error: any) => {
      // Clear processing state on error
      setProcessingOrgId(null);
      toast.error("Failed to send collaboration request", {
        description: error?.message ?? "Please try again later.",
      });
    },
  });

  // Mutation for updating collaboration status (Accept)
  const updateCollaborationStatusMutation = useMutation({
    mutationFn: async (payload: {
      collaborationId: string;
      status: number;
    }) => {
      return await CityCollaborationService.updateCollaborationStatus(
        payload.collaborationId,
        payload.status
      );
    },
    onSuccess: () => {
      // Clear accepting state
      setAcceptingCollaborationId(null);
      toast.success("Collaboration request accepted!", {
        description: "You can now access the collaboration feed.",
        icon: <CheckCircle className="text-success h-4 w-4" />,
      });
      // Refetch collaborations to get updated status
      queryClient.invalidateQueries({ queryKey: ["city-collaborations"] });
      queryClient.invalidateQueries({
        queryKey: ["city-collaborations-count"],
      });
      queryClient.invalidateQueries({ queryKey: ["city-collaborations-orgs"] });
    },
    onError: (error: any) => {
      // Clear accepting state on error
      setAcceptingCollaborationId(null);
      toast.error("Failed to accept collaboration request", {
        description: error?.message ?? "Please try again later.",
      });
    },
  });

  // Mutation for rejecting collaboration request
  const rejectCollaborationMutation = useMutation({
    mutationFn: async (collaborationId: string) => {
      return await CityCollaborationService.rejectCollaboration(
        collaborationId
      );
    },
    onSuccess: () => {
      // Clear rejecting state
      setRejectingCollaborationId(null);
      toast.success("Collaboration request rejected", {
        description: "The request has been declined.",
        icon: <XCircle className="h-4 w-4" />,
      });
      // Refetch collaborations and organizations to get updated status
      queryClient.invalidateQueries({ queryKey: ["city-collaborations"] });
      queryClient.invalidateQueries({
        queryKey: ["city-collaborations-count"],
      });
      queryClient.invalidateQueries({ queryKey: ["city-collaborations-orgs"] });
    },
    onError: (error: any) => {
      // Clear rejecting state on error
      setRejectingCollaborationId(null);
      toast.error("Failed to reject collaboration request", {
        description: error?.message ?? "Please try again later.",
      });
    },
  });

  // Mutation for withdrawing/canceling/deactivating collaboration request
  const withdrawCollaborationMutation = useMutation({
    mutationFn: async (collaborationId: string) => {
      return await CityCollaborationService.withdrawCollaboration(
        collaborationId
      );
    },
    onSuccess: (_, collaborationId) => {
      // Clear canceling and deactivating states
      setCancelingCollaborationId(null);
      setDeactivatingCollaborationId(null);

      // Show appropriate toast message based on context
      if (deactivatingCollaborationId === collaborationId) {
        toast.success("Collaboration deactivated", {
          description: "The collaboration has been deactivated successfully.",
        });
      } else {
        toast.success("Collaboration request canceled", {
          description: "The request has been withdrawn successfully.",
        });
      }

      // Refetch collaborations and organizations to get updated status
      queryClient.invalidateQueries({ queryKey: ["city-collaborations"] });
      queryClient.invalidateQueries({
        queryKey: ["city-collaborations-count"],
      });
      queryClient.invalidateQueries({ queryKey: ["city-collaborations-orgs"] });
    },
    onError: (error: any) => {
      // Clear canceling and deactivating states on error
      setCancelingCollaborationId(null);
      setDeactivatingCollaborationId(null);
      toast.error("Failed to process request", {
        description: error?.message ?? "Please try again later.",
      });
    },
  });

  const handleCreateCollaboration = (partnerId: string) => {
    // Get login_org_id from collaboration counts API response
    const sender_organization_id = loginOrgId;
    const receiver_organization_id = partnerId;

    // Console logs as requested
    console.log(
      "login_org_id from count API as sender_organization_id:",
      sender_organization_id
    );
    console.log(
      "org_id as receiver_organization_id:",
      receiver_organization_id
    );

    if (!sender_organization_id) {
      toast.error("Unable to send request", {
        description:
          "Organization information not available. Please refresh the page.",
      });
      return;
    }

    // Set processing state for this specific organization
    setProcessingOrgId(partnerId);

    const payload = {
      sender_organization_id,
      receiver_organization_id,
    };

    console.log("API Payload:", payload);

    createCollaborationMutation.mutate(payload);
  };

  const handleUpdateStatus = ({
    id,
    status,
  }: {
    id: string;
    status: CollaborationStatus;
  }) => {
    // If accepting, call the API with status: 2
    if (status === "active") {
      setAcceptingCollaborationId(id);
      updateCollaborationStatusMutation.mutate({
        collaborationId: id,
        status: 2,
      });
      return;
    }

    // If rejecting, call the reject API endpoint
    if (status === "ended") {
      setRejectingCollaborationId(id);
      rejectCollaborationMutation.mutate(id);
      return;
    }

    // For other statuses, just update local state
    setCollaborations(prev =>
      prev.map(collab => {
        if (collab.id === id) {
          return {
            ...collab,
            status,
          };
        }
        return collab;
      })
    );
  };

  // Organizations are already filtered by the API based on searchQuery
  const filteredOrganizations = organizations;

  // Filter active collaborations from API (status: 2 means accepted)
  // Only show collaborations where the logged-in organization is involved (as sender or receiver)
  // These should only appear in Active Collaborations section
  const activeCollaborations = apiCollaborations.filter(
    (c: CollaborationResponse) => {
      // Check if collaboration is accepted/active
      const isActive =
        c.status === 2 ||
        c.status_label?.toLowerCase().includes("accepted") ||
        c.status_label?.toLowerCase().includes("active");

      // Check if logged-in organization is involved (either sender or receiver)
      const isInvolved =
        loginOrgId &&
        (c.sender_organization_id === loginOrgId ||
          c.receiver_organization_id === loginOrgId);

      return isActive && isInvolved;
    }
  );

  // Separate API collaborations into sent and incoming requests
  // Exclude accepted collaborations (status: 2) - they should only show in Active Collaborations
  // Exclude withdrawn collaborations (status: 4) - they should not show in Sent Requests or Incoming Requests
  // Compare login_org_id from count API:
  // If sender_organization_id === login_org_id: Sent Requests (we sent it)
  // If receiver_organization_id === login_org_id: Incoming Requests (someone sent it to us)
  const isAccepted = (c: CollaborationResponse) =>
    c.status === 2 ||
    c.status_label?.toLowerCase().includes("accepted") ||
    c.status_label?.toLowerCase().includes("active");

  const isWithdrawn = (c: CollaborationResponse) =>
    c.status === 4 || c.status_label?.toLowerCase().includes("withdrawn");

  const isRejected = (c: CollaborationResponse) =>
    c.status === 3 || c.status_label?.toLowerCase().includes("rejected");

  const isPending = (c: CollaborationResponse) =>
    c.status === 1 ||
    c.status_label?.toLowerCase().includes("pending") ||
    c.status_label?.toLowerCase().includes("request sent") ||
    c.status_label?.toLowerCase().includes("awaiting");

  const sentRequests = apiCollaborations.filter(
    (c: CollaborationResponse) =>
      loginOrgId &&
      c.sender_organization_id === loginOrgId &&
      !isAccepted(c) &&
      !isWithdrawn(c) &&
      !isRejected(c) &&
      isPending(c) // Only show pending sent requests
  );
  const incomingRequests = apiCollaborations.filter(
    (c: CollaborationResponse) =>
      loginOrgId &&
      c.receiver_organization_id === loginOrgId &&
      !isAccepted(c) &&
      !isWithdrawn(c) &&
      !isRejected(c) &&
      isPending(c) && // Only show pending incoming requests
      c.status !== null && // Exclude null status
      c.status !== undefined && // Exclude undefined status
      c.status_label !== null && // Exclude null status_label
      c.status_label !== undefined && // Exclude undefined status_label
      c.status_label !== "" // Exclude empty status_label
  );

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="space-y-4 p-2 sm:space-y-6 sm:p-4">
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="bg-info/10 flex h-8 w-8 items-center justify-center rounded-lg sm:h-10 sm:w-10">
            <Handshake className="text-info h-4 w-4 sm:h-5 sm:w-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground sm:text-2xl">
              City Collaborations
            </h1>
            <p className="text-xs text-muted-foreground sm:text-sm">
              Manage partnerships and collaboration requests
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {incomingRequests.length > 0 && (
            <Badge variant="secondary" className="gap-1 text-xs">
              <Bell className="h-3 w-3" />
              {incomingRequests.length} pending
            </Badge>
          )}
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList className="grid h-9 w-full grid-cols-2 sm:h-10">
          <TabsTrigger
            value="organizations"
            className="flex items-center gap-1 text-xs sm:gap-2 sm:text-sm"
          >
            <Building2 className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="xs:inline hidden">Organizations</span>
            <span className="xs:hidden">Orgs</span>
          </TabsTrigger>
          <TabsTrigger
            value="requests"
            className="flex items-center gap-1 text-xs sm:gap-2 sm:text-sm"
          >
            <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="xs:inline hidden">Requests</span>
            <span className="xs:hidden">Reqs</span>
            {incomingRequests.length > 0 && (
              <Badge
                variant="destructive"
                className="flex h-4 w-4 items-center justify-center p-0 text-[10px] sm:h-5 sm:w-5 sm:text-xs"
              >
                {incomingRequests.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Organizations Tab */}
        <TabsContent value="organizations" className="space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
            <Card className="py-3 text-center sm:py-4">
              <CardContent className="p-0">
                <p className="text-xl font-bold text-primary sm:text-2xl">
                  {isLoadingCounts
                    ? "..."
                    : isErrorCounts || !collaborationCounts
                      ? "XXX"
                      : collaborationCounts.accepted}
                </p>
                <p className="text-xs text-muted-foreground sm:text-sm">
                  Accepted
                </p>
              </CardContent>
            </Card>
            <Card className="py-3 text-center sm:py-4">
              <CardContent className="p-0">
                <p className="text-xl font-bold text-primary sm:text-2xl">
                  {isLoadingCounts
                    ? "..."
                    : isErrorCounts || !collaborationCounts
                      ? "XXX"
                      : collaborationCounts.request_pending}
                </p>
                <p className="text-xs text-muted-foreground sm:text-sm">
                  Request Pending
                </p>
              </CardContent>
            </Card>
            <Card className="py-3 text-center sm:py-4">
              <CardContent className="p-0">
                <p className="text-xl font-bold text-primary sm:text-2xl">
                  {isLoadingCounts
                    ? "..."
                    : isErrorCounts || !collaborationCounts
                      ? "XXX"
                      : collaborationCounts.acceptance_pending}
                </p>
                <p className="text-xs text-muted-foreground sm:text-sm">
                  Acceptance Pending
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground sm:left-3 sm:h-4 sm:w-4" />
            <Input
              placeholder="Search organizations..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-8 text-sm sm:pl-10"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {isLoadingOrgs ? (
              <div className="col-span-full py-8 text-center text-muted-foreground">
                <Building2 className="mx-auto mb-4 h-12 w-12 animate-pulse opacity-50" />
                <p>Loading organizations...</p>
              </div>
            ) : (
              filteredOrganizations.map(org => {
                // Check collaborations for this organization
                const orgCollaborations = apiCollaborations.filter(
                  (c: CollaborationResponse) =>
                    c.sender_organization_id === org.org_id ||
                    c.receiver_organization_id === org.org_id
                );

                // Check if there's an active/accepted collaboration (status: 2)
                const hasActiveCollaboration = orgCollaborations.some(
                  (c: CollaborationResponse) => c.status === 2
                );

                // Check if there's a pending collaboration where we sent the request (sender side)
                // This means a request was sent by us but not yet accepted/rejected/withdrawn
                // Also check if we're currently processing this organization
                const hasPendingSentCollaboration =
                  orgCollaborations.some(
                    (c: CollaborationResponse) =>
                      loginOrgId &&
                      c.sender_organization_id === loginOrgId &&
                      c.receiver_organization_id === org.org_id &&
                      c.status !== 2 &&
                      c.status !== 3 &&
                      c.status !== 4 &&
                      (c.status === 1 ||
                        c.status_label?.toLowerCase().includes("pending") ||
                        c.status_label
                          ?.toLowerCase()
                          .includes("request sent") ||
                        c.status_label?.toLowerCase().includes("awaiting"))
                  ) ||
                  (processingOrgId === org.org_id &&
                    createCollaborationMutation.isPending);

                // Check if there's a pending collaboration where we received the request (receiver side)
                // This means they sent a request to us but not yet accepted/rejected/withdrawn
                const hasPendingReceivedCollaboration = orgCollaborations.some(
                  (c: CollaborationResponse) =>
                    loginOrgId &&
                    c.receiver_organization_id === loginOrgId &&
                    c.sender_organization_id === org.org_id &&
                    c.status !== 2 &&
                    c.status !== 3 &&
                    c.status !== 4 &&
                    (c.status === 1 ||
                      c.status_label?.toLowerCase().includes("pending") ||
                      c.status_label?.toLowerCase().includes("request sent") ||
                      c.status_label?.toLowerCase().includes("awaiting"))
                );

                // Combined check for any pending collaboration
                const hasPendingCollaboration =
                  hasPendingSentCollaboration ||
                  hasPendingReceivedCollaboration;

                // Check if there's a withdrawn collaboration (status: 4) and no active one
                const hasOnlyWithdrawnCollaboration =
                  orgCollaborations.some(
                    (c: CollaborationResponse) => c.status === 4
                  ) && !hasActiveCollaboration;

                // Check if there's a rejected collaboration (status: 3) and no active one
                const hasOnlyRejectedCollaboration =
                  orgCollaborations.some((c: CollaborationResponse) =>
                    isRejected(c)
                  ) && !hasActiveCollaboration;

                // Check if there's any rejected collaboration
                const hasRejectedCollaboration = orgCollaborations.some(
                  (c: CollaborationResponse) => isRejected(c)
                );

                // Check if organization has status = 2 (Accepted) from the organizations API
                const orgStatusAccepted =
                  org.status === "2" || String(org.status) === "2";

                // Get status label from API response (only use if there's an active collaboration and not withdrawn/rejected)
                // Also don't show status if there's a rejected collaboration
                const shouldShowStatus =
                  (hasActiveCollaboration || orgStatusAccepted) &&
                  !hasRejectedCollaboration &&
                  org.status_label !== null &&
                  org.status_label !== undefined &&
                  !org.status_label.toLowerCase().includes("withdrawn") &&
                  !org.status_label.toLowerCase().includes("rejected");
                const statusLabel =
                  shouldShowStatus && org.status_label ? org.status_label : "";

                // Show Send Request button if:
                // 1. No active collaboration exists, AND
                // 2. Organization status is not 2 (Accepted), AND
                // 3. No pending collaboration exists, AND
                // 4. There's only a withdrawn/rejected collaboration (no active one) OR no collaboration at all
                const shouldShowSendRequest =
                  !hasActiveCollaboration &&
                  !orgStatusAccepted &&
                  !hasPendingCollaboration &&
                  (hasOnlyWithdrawnCollaboration ||
                    hasOnlyRejectedCollaboration ||
                    hasRejectedCollaboration ||
                    orgCollaborations.length === 0);

                return (
                  <Card
                    key={org.org_id}
                    className="transition-shadow hover:shadow-md"
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-12 w-12 bg-primary/10">
                            <AvatarFallback className="bg-primary/10 font-semibold text-primary">
                              {getInitials(org.org_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <CardTitle className="text-base">
                              {org.org_name}
                            </CardTitle>
                            <CardDescription className="text-xs">
                              {org.website ??
                                org.primaryemail ??
                                "No website specified"}
                            </CardDescription>
                          </div>
                        </div>
                        {org.organizationtype_name && (
                          <Badge
                            variant="outline"
                            className="text-xs capitalize"
                          >
                            {org.organizationtype_name}
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="mb-4 text-sm text-muted-foreground">
                        {org.description ??
                          org.location ??
                          "Government organization available for inter-city collaboration and resource sharing."}
                      </p>
                      {shouldShowSendRequest ? (
                        <Button
                          className="w-full"
                          onClick={() => handleCreateCollaboration(org.org_id)}
                          disabled={
                            processingOrgId === org.org_id &&
                            createCollaborationMutation.isPending
                          }
                        >
                          {processingOrgId === org.org_id &&
                          createCollaborationMutation.isPending ? (
                            <>
                              <Clock className="mr-2 h-4 w-4 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="mr-2 h-4 w-4" />
                              Send Request
                            </>
                          )}
                        </Button>
                      ) : hasPendingSentCollaboration ? (
                        <Badge className={statusColors.pending}>
                          <Clock className="mr-1 h-3 w-3" />
                          Request sent
                        </Badge>
                      ) : hasPendingReceivedCollaboration ? (
                        <Badge className={statusColors.pending}>
                          <Bell className="mr-1 h-3 w-3" />
                          Request Received
                        </Badge>
                      ) : orgStatusAccepted ? (
                        <Badge className={statusColors.active}>
                          <CheckCircle className="mr-1 h-3 w-3" />
                          Accepted
                        </Badge>
                      ) : statusLabel ? (
                        <Badge
                          className={
                            statusLabel.toLowerCase().includes("active")
                              ? statusColors.active
                              : statusLabel.toLowerCase().includes("pending")
                                ? statusColors.pending
                                : statusColors.pending
                          }
                        >
                          {statusLabel.toLowerCase().includes("active") ? (
                            <>
                              <CheckCircle className="mr-1 h-3 w-3" />{" "}
                              {statusLabel}
                            </>
                          ) : (
                            <>
                              <Clock className="mr-1 h-3 w-3" /> {statusLabel}
                            </>
                          )}
                        </Badge>
                      ) : (
                        <Button
                          className="w-full"
                          onClick={() => handleCreateCollaboration(org.org_id)}
                          disabled={
                            processingOrgId === org.org_id &&
                            createCollaborationMutation.isPending
                          }
                        >
                          {processingOrgId === org.org_id &&
                          createCollaborationMutation.isPending ? (
                            <>
                              <Clock className="mr-2 h-4 w-4 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="mr-2 h-4 w-4" />
                              Send Request
                            </>
                          )}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>

          {!isLoadingOrgs && filteredOrganizations.length === 0 && (
            <Card className="py-12">
              <CardContent className="text-center text-muted-foreground">
                <Building2 className="mx-auto mb-4 h-12 w-12 opacity-50" />
                {searchQuery ? (
                  <p>No organizations found matching your search.</p>
                ) : (
                  <p>Please search to find organizations.</p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Requests Tab */}
        <TabsContent value="requests" className="space-y-4">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Incoming Requests */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-primary" />
                  Incoming Requests
                </CardTitle>
                <CardDescription>
                  Review and respond to collaboration requests
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {isLoadingCollaborations ? (
                  <div className="py-8 text-center text-muted-foreground">
                    <Clock className="mx-auto mb-2 h-10 w-10 animate-pulse opacity-50" />
                    <p>Loading requests...</p>
                  </div>
                ) : incomingRequests.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground">
                    <Clock className="mx-auto mb-2 h-10 w-10 opacity-50" />
                    <p>No pending requests</p>
                  </div>
                ) : (
                  incomingRequests.map((req: CollaborationResponse) => (
                    <div key={req.id} className="rounded-lg border bg-card p-4">
                      <div className="mb-3 flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {getInitials(
                                req.sender_organization_name ?? "UN"
                              )}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">
                              Collaboration with{" "}
                              {req.receiver_organization_name}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              From: {req.sender_organization_name}
                            </p>
                          </div>
                        </div>
                        <Badge className={statusColors.pending}>
                          <Clock className="mr-1 h-3 w-3" />
                          {req.status_label}
                        </Badge>
                      </div>
                      <div className="mb-3 flex flex-wrap gap-1">
                        {req.description && (
                          <Badge variant="outline" className="text-xs">
                            {req.description}
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1 bg-green-600 text-white hover:bg-green-700"
                          onClick={() =>
                            handleUpdateStatus({ id: req.id, status: "active" })
                          }
                          disabled={
                            acceptingCollaborationId === req.id ||
                            updateCollaborationStatusMutation.isPending
                          }
                        >
                          {acceptingCollaborationId === req.id ||
                          updateCollaborationStatusMutation.isPending ? (
                            <>
                              <Clock className="mr-1 h-4 w-4 animate-spin" />
                              Accepting...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="mr-1 h-4 w-4" />
                              Accept
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 hover:border-red-600 hover:bg-red-600 hover:text-white"
                          onClick={() =>
                            handleUpdateStatus({ id: req.id, status: "ended" })
                          }
                          disabled={
                            rejectingCollaborationId === req.id ||
                            rejectCollaborationMutation.isPending
                          }
                        >
                          {rejectingCollaborationId === req.id ||
                          rejectCollaborationMutation.isPending ? (
                            <>
                              <Clock className="mr-1 h-4 w-4 animate-spin" />
                              Rejecting...
                            </>
                          ) : (
                            <>
                              <XCircle className="mr-1 h-4 w-4" />
                              Reject
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Sent Requests */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="text-info h-5 w-5" />
                  Sent Requests
                </CardTitle>
                <CardDescription>
                  Track your outgoing collaboration requests
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {isLoadingCollaborations ? (
                  <div className="py-8 text-center text-muted-foreground">
                    <Send className="mx-auto mb-2 h-10 w-10 animate-pulse opacity-50" />
                    <p>Loading requests...</p>
                  </div>
                ) : sentRequests.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground">
                    <Send className="mx-auto mb-2 h-10 w-10 opacity-50" />
                    <p>No sent requests</p>
                  </div>
                ) : (
                  sentRequests.map((req: CollaborationResponse) => (
                    <div key={req.id} className="rounded-lg border bg-card p-4">
                      <div className="mb-3 flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-info/10 text-info">
                              {getInitials(
                                req.receiver_organization_name ?? "UN"
                              )}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">
                              Collaboration with {req.sender_organization_name}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              To: {req.receiver_organization_name}
                            </p>
                          </div>
                        </div>
                        <Badge className={statusColors.pending}>
                          <Clock className="mr-1 h-3 w-3" />
                          Awaiting Response
                        </Badge>
                      </div>
                      <p className="mb-3 text-xs text-muted-foreground">
                        Sent:{" "}
                        {req.created_on
                          ? format(
                              new Date(req.created_on),
                              "MMM d, yyyy 'at' h:mm a"
                            )
                          : "—"}
                      </p>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="w-full"
                        onClick={() => {
                          setCancelingCollaborationId(req.id);
                          withdrawCollaborationMutation.mutate(req.id);
                        }}
                        disabled={
                          cancelingCollaborationId === req.id &&
                          withdrawCollaborationMutation.isPending
                        }
                      >
                        {cancelingCollaborationId === req.id &&
                        withdrawCollaborationMutation.isPending ? (
                          <>
                            <Clock className="mr-1 h-4 w-4 animate-spin" />
                            Canceling...
                          </>
                        ) : (
                          "Cancel Request"
                        )}
                      </Button>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Active Collaborations */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="text-success h-5 w-5" />
                Active Collaborations
              </CardTitle>
              <CardDescription>Currently active partnerships</CardDescription>
            </CardHeader>
            <CardContent>
              {activeCollaborations.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  <Handshake className="mx-auto mb-2 h-10 w-10 opacity-50" />
                  <p>No active collaborations yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {activeCollaborations.map((collab: CollaborationResponse) => {
                    // Determine partner organization name
                    // If we're the sender, show receiver name; if we're the receiver, show sender name
                    const isSender =
                      loginOrgId &&
                      collab.sender_organization_id === loginOrgId;
                    const partnerOrgName = isSender
                      ? collab.receiver_organization_name
                      : collab.sender_organization_name;

                    return (
                      <div
                        key={collab.id}
                        className="bg-success/5 border-success/20 rounded-lg border p-4"
                      >
                        <div className="mb-3 flex items-center gap-3">
                          <div className="bg-success/10 flex h-10 w-10 items-center justify-center rounded-full">
                            <CheckCircle className="text-success h-5 w-5" />
                          </div>
                          <div>
                            <p className="font-medium">
                              Collaboration with {partnerOrgName}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {isSender
                                ? `To: ${partnerOrgName}`
                                : `From: ${partnerOrgName}`}
                            </p>
                          </div>
                        </div>
                        <p className="mb-3 text-xs text-muted-foreground">
                          Active since:{" "}
                          {collab.accepted_at
                            ? format(
                                new Date(collab.accepted_at),
                                "MMM d, yyyy"
                              )
                            : collab.created_on
                              ? format(
                                  new Date(collab.created_on),
                                  "MMM d, yyyy"
                                )
                              : "—"}
                        </p>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="w-full"
                          onClick={() => {
                            setDeactivatingCollaborationId(collab.id);
                            withdrawCollaborationMutation.mutate(collab.id);
                          }}
                          disabled={
                            deactivatingCollaborationId === collab.id ||
                            withdrawCollaborationMutation.isPending
                          }
                        >
                          {deactivatingCollaborationId === collab.id ||
                          withdrawCollaborationMutation.isPending ? (
                            <>
                              <Clock className="mr-1 h-4 w-4 animate-spin" />
                              Deactivating...
                            </>
                          ) : (
                            <>
                              <XCircle className="mr-1 h-4 w-4" />
                              Deactivate
                            </>
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
